import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, Modal, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import {
  errorNotification,
  getRequest,
  patchRequest,
  postRequest,
  successNotification,
  vehicleHistoryProductPreviewUrl,
  vehicleHistoryProductUrl,
  vehicleHistoryProductsUrl,
  vehicleHistoryServicesUrl
} from '../../config'
import { deleteQuiet, uploadPreview } from './api'
import ProductFormDrawer from './ProductFormDrawer'
import { MarginTag } from './tags'
import {
  IDENTIFIER_LABELS,
  MARKET_LABELS,
  MARKET_ORDER,
  activationSentences,
  money,
  serverActivationErrors,
  serverMessage,
  usd
} from './utils'

// The owner's pricing screen. Every package we could sell, what each costs us, what he charges,
// and what he makes — grouped by market, because that is how he thinks about them.
//
// **Nothing here is on sale by default.** Every package ships off, so the "On sale" column is
// mostly grey and that is the normal state, not a fault. The switch refuses with sentences rather
// than codes when a package is not ready, so he fixes it in one pass instead of four.
const ProductsPage = () => {
  const [products, setProducts] = useState([])
  const [services, setServices] = useState([])
  const [currencies, setCurrencies] = useState(['GHS', 'USD'])
  const [exchangeRate, setExchangeRate] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const load = useCallback(async () => {
    const [productsResponse, servicesResponse] = await Promise.all([
      getRequest(vehicleHistoryProductsUrl, 'report packages', setLoading),
      getRequest(vehicleHistoryServicesUrl, 'supplier reports', () => {})
    ])
    if (productsResponse.success) {
      const data = productsResponse.data || {}
      setProducts(data.results || [])
      setExchangeRate(data.exchangeRate || null)
      if (data.currencies && data.currencies.length) setCurrencies(data.currencies)
    }
    if (servicesResponse.success) {
      setServices((servicesResponse.data || {}).results || [])
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // The switch. A refusal is shown as sentences and the switch stays where it was — the owner
  // should never be left looking at an "On sale" toggle for a package that is not.
  const toggleActive = async (product, active) => {
    const response = await patchRequest(
      vehicleHistoryProductUrl(product.id),
      { active },
      'the package',
      setSaving
    )
    if (response.success) {
      successNotification(active ? `${product.name} is on sale.` : `${product.name} is off.`)
      load()
      return
    }
    const reasons = activationSentences(serverActivationErrors(response))
    Modal.warning({
      title: 'This package cannot go on sale yet',
      content: reasons.length ? (
        <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
          {reasons.map(sentence => (
            <li key={sentence}>{sentence}</li>
          ))}
        </ul>
      ) : (
        serverMessage(response)
      )
    })
  }

  const confirmOff = product => {
    Modal.confirm({
      title: `Take ${product.name} off sale?`,
      content:
        'New customers will not see it. Paid orders for this report will still be completed.',
      okText: 'Take it off',
      onOk: () => toggleActive(product, false)
    })
  }

  const save = async values => {
    const response = editing && editing.id
      ? await patchRequest(vehicleHistoryProductUrl(editing.id), values, 'the package', setSaving)
      : await postRequest(vehicleHistoryProductsUrl, values, 'the package', setSaving)
    if (!response.success) {
      errorNotification(serverMessage(response, 'Could not save the package.'))
      return
    }
    successNotification('Saved.')
    setDrawerOpen(false)
    setEditing(null)
    load()
  }

  const onUploadPreview = async file => {
    if (!editing || !editing.id) return
    setSaving(true)
    const response = await uploadPreview(vehicleHistoryProductPreviewUrl(editing.id), file)
    setSaving(false)
    if (!response.success) {
      errorNotification(serverMessage(response, 'Could not upload that image.'))
      return
    }
    setEditing(response.data)
    successNotification('Preview updated.')
    load()
  }

  const onRemovePreview = async () => {
    if (!editing || !editing.id) return
    setSaving(true)
    const response = await deleteQuiet(vehicleHistoryProductPreviewUrl(editing.id))
    setSaving(false)
    if (!response.success) {
      errorNotification(serverMessage(response, 'Could not remove that image.'))
      return
    }
    setEditing({ ...editing, previewImageUrl: null })
    load()
  }

  // A column factory, with the house's scoped disable: an anonymous arrow inside an antd `render`
  // trips react/display-name, and naming each one would be seven components nobody calls by name.
  /* eslint-disable react/display-name */
  const columns = [
    {
      title: 'Package',
      key: 'name',
      render: row => (
        <div>
          <div>
            <Typography.Text strong>{row.name}</Typography.Text>{' '}
            {row.recommended ? <Tag color="blue">Most popular</Tag> : null}
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.key} · {IDENTIFIER_LABELS[row.identifierKind] || row.identifierKind}
          </Typography.Text>
        </div>
      )
    },
    {
      title: 'Reports in it',
      key: 'components',
      render: row =>
        (row.components || []).length
          ? (row.components || []).map(component => component.label).join(' · ')
          : <Typography.Text type="secondary">None yet</Typography.Text>
    },
    {
      title: 'Costs us',
      key: 'cost',
      render: row => (
        <div>
          <div>{row.costUsd === null ? '—' : usd(row.costUsd)}</div>
          {row.costInPriceCurrency ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {money(row.costInPriceCurrency, row.priceCurrency)}
            </Typography.Text>
          ) : null}
        </div>
      )
    },
    {
      title: 'Customer pays',
      key: 'price',
      render: row =>
        row.priceAmount === null ? (
          <Typography.Text type="warning">Not priced</Typography.Text>
        ) : (
          money(row.priceAmount, row.priceCurrency)
        )
    },
    {
      title: 'Margin',
      key: 'margin',
      render: row => (
        <Space direction="vertical" size={0}>
          <MarginTag marginPercent={row.marginPercent} floor={row.minMarginApplied} />
          {row.marginAmount ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {money(row.marginAmount, row.priceCurrency)}
            </Typography.Text>
          ) : null}
        </Space>
      )
    },
    {
      title: 'On sale',
      key: 'active',
      render: row => {
        const blocked = (row.activationErrors || []).length > 0
        const control = (
          <Switch
            checked={row.active}
            disabled={saving || (blocked && !row.active)}
            onChange={checked => (checked ? toggleActive(row, true) : confirmOff(row))}
          />
        )
        if (!blocked || row.active) return control
        return (
          <Tooltip
            title={
              <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
                {activationSentences(row.activationErrors).map(sentence => (
                  <li key={sentence}>{sentence}</li>
                ))}
              </ul>
            }
          >
            <span>{control}</span>
          </Tooltip>
        )
      }
    },
    {
      title: '',
      key: 'edit',
      render: row => (
        <Button
          type="link"
          onClick={() => {
            setEditing(row)
            setDrawerOpen(true)
          }}
        >
          Edit
        </Button>
      )
    }
  ]

  /* eslint-enable react/display-name */

  // Grouped by market, in the order the markets matter to us — China is 32% of our paying
  // customers' cars, which no alphabetical sort would ever show.
  const groups = MARKET_ORDER.map(market => ({
    market,
    rows: products.filter(product => product.market === market)
  })).filter(group => group.rows.length)

  const unpriced = products.filter(product => product.priceAmount === null).length
  const live = products.filter(product => product.active).length

  return (
    <div>
      <Card
        title="Report packages"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing({})
                setDrawerOpen(true)
              }}
            >
              New package
            </Button>
          </Space>
        }
      >
        {live === 0 ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Nothing is on sale yet"
            description={
              <span>
                Every package ships switched off. Set a price, check the margin, then switch one on.
                {unpriced ? ` ${unpriced} of them have no price yet.` : ''}
              </span>
            }
          />
        ) : null}

        {(exchangeRate || {}).ghsToUsd ? (
          <Typography.Paragraph type="secondary">
            Cedi figures worked out at {Number(exchangeRate.ghsToUsd).toFixed(2)} to the dollar,
            stored {exchangeRate.at ? new Date(exchangeRate.at).toLocaleDateString() : 'recently'}.
          </Typography.Paragraph>
        ) : null}

        {groups.map(group => (
          <div key={group.market} style={{ marginBottom: 24 }}>
            <Typography.Title level={5}>{MARKET_LABELS[group.market]}</Typography.Title>
            <Table
              rowKey="id"
              size="small"
              loading={loading}
              dataSource={group.rows}
              columns={columns}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </div>
        ))}

        {!loading && !products.length ? (
          <Alert
            type="warning"
            showIcon
            message="No packages yet"
            description="Run `manage.py seed_vehicle_history` on the API to create the catalogue."
          />
        ) : null}
      </Card>

      <ProductFormDrawer
        open={drawerOpen}
        product={editing}
        services={services}
        currencies={currencies}
        exchangeRate={exchangeRate}
        saving={saving}
        onClose={() => {
          setDrawerOpen(false)
          setEditing(null)
        }}
        onSave={save}
        onUploadPreview={onUploadPreview}
        onRemovePreview={onRemovePreview}
      />
    </div>
  )
}

export default ProductsPage
